const menuButton = document.querySelector('[data-menu-toggle]');
const mobileMenu = document.querySelector('[data-mobile-menu]');

if (menuButton && mobileMenu) {
  let closeTimer;
  const setMenu = (open) => {
    window.clearTimeout(closeTimer);
    menuButton.setAttribute('aria-expanded', String(open));
    if (open) {
      mobileMenu.hidden = false;
      window.requestAnimationFrame(() => mobileMenu.classList.add('is-open'));
      mobileMenu.querySelector('a')?.focus({ preventScroll: true });
      return;
    }
    mobileMenu.classList.remove('is-open');
    closeTimer = window.setTimeout(() => { mobileMenu.hidden = true; }, 240);
  };
  menuButton.addEventListener('click', () => setMenu(menuButton.getAttribute('aria-expanded') !== 'true'));
  mobileMenu.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setMenu(false)));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && menuButton.getAttribute('aria-expanded') === 'true') {
      setMenu(false);
      menuButton.focus();
    }
  });
}

const formatMoney = (amountInCents, currency = window.Shopify?.currency?.active || 'INR') => new Intl.NumberFormat(
  document.documentElement.lang || 'en-IN',
  { style: 'currency', currency, maximumFractionDigits: 2 }
).format(amountInCents / 100);

document.querySelectorAll('[data-quantity-step]').forEach((button) => {
  button.addEventListener('click', () => {
    const input = button.parentElement.querySelector('input[type="number"]');
    if (!input || input.disabled) return;
    const minimum = Number(input.min || 1);
    const maximum = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
    const direction = Number(button.dataset.quantityStep);
    const increment = Math.max(1, Number(input.step || 1));
    input.value = Math.min(maximum, Math.max(minimum, Number(input.value || minimum) + (direction * increment)));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
});

const productCommerce = document.querySelector('[data-product-commerce]');
if (productCommerce) {
  const productFormWrap = productCommerce.querySelector('[data-product-form]');
  const productForm = productFormWrap?.querySelector('form[action*="/cart/add"]');
  const quantity = productForm?.querySelector('[data-product-quantity]');
  const variantInput = productForm?.querySelector('[data-variant-id]');
  const variantOptions = [...(productForm?.querySelectorAll('[data-variant-option]') || [])];
  const total = productCommerce.querySelector('[data-product-total]');
  const compare = productCommerce.querySelector('[data-product-compare]');
  const compareWrap = productCommerce.querySelector('[data-product-compare-wrap]');
  const discount = productCommerce.querySelector('[data-product-discount]');
  const status = productCommerce.querySelector('[data-commerce-status]');
  const error = productCommerce.querySelector('[data-commerce-error]');
  const addButton = productCommerce.querySelector('[data-add-to-cart]');
  let snapshot;
  let verifiedSubmit = false;
  let refreshSequence = 0;

  const selectedVariant = () => snapshot?.variants.find((item) => String(item.id) === String(variantInput?.value));
  const copy = (name, fallback) => productCommerce.dataset[name] || fallback;
  const selectedOption = () => variantOptions.find((option) => option.checked);
  const quantityRuleFromOption = (variantId) => {
    const option = variantOptions.find((item) => String(item.value) === String(variantId));
    if (!option) return null;
    return {
      min: Math.max(1, Number(option.dataset.quantityMin || 1)),
      increment: Math.max(1, Number(option.dataset.quantityIncrement || 1)),
      max: option.dataset.quantityMax ? Number(option.dataset.quantityMax) : null
    };
  };

  const setLoading = (loading) => {
    if (!addButton) return;
    addButton.classList.toggle('is-loading', loading);
    addButton.toggleAttribute('aria-busy', loading);
    if (loading) addButton.querySelector('span').textContent = copy('addingLabel', 'Adding to cart…');
  };

  const renderProduct = () => {
    const current = selectedVariant();
    if (!current || !quantity || !total || !addButton) return;
    const rule = current.quantityRule || { min: 1, increment: 1, max: null };
    const minimum = Math.max(1, Number(rule.min || 1));
    const increment = Math.max(1, Number(rule.increment || 1));
    const maximum = rule.max ? Number(rule.max) : Number.POSITIVE_INFINITY;
    const rawQuantity = Math.max(minimum, Number(quantity.value || minimum));
    const steppedQuantity = minimum + (Math.round((rawQuantity - minimum) / increment) * increment);
    const requested = Math.min(maximum, Math.max(minimum, steppedQuantity));
    const canBuy = Boolean(current.available);
    quantity.value = String(requested);
    quantity.min = String(minimum);
    quantity.step = String(increment);
    if (Number.isFinite(maximum)) quantity.max = String(maximum);
    else quantity.removeAttribute('max');
    total.textContent = formatMoney(current.price, snapshot.currency);
    if (compare) {
      const comparePrice = Number(current.compareAtPrice || 0);
      compare.textContent = comparePrice > current.price ? formatMoney(comparePrice, snapshot.currency) : '';
      compareWrap?.classList.toggle('is-hidden', comparePrice <= current.price);
      if (discount) {
        const percent = comparePrice > current.price ? Math.round(((comparePrice - current.price) / comparePrice) * 100) : 0;
        discount.textContent = percent > 0 ? `${percent}% off` : '';
        discount.classList.toggle('is-hidden', percent <= 0);
      }
    }
    quantity.disabled = !canBuy;
    addButton.disabled = !canBuy;
    setLoading(false);
    addButton.querySelector('span').textContent = canBuy ? copy('addLabel', 'Add to cart') : copy('soldOutLabel', 'Sold out');
    status.textContent = canBuy ? copy('confirmedLabel', 'In stock — live availability confirmed.') : copy('soldOutStatus', 'This option is currently sold out.');
    productCommerce.classList.remove('is-checking');
    productCommerce.classList.toggle('is-sold-out', !canBuy);
    error?.classList.add('is-hidden');
  };

  const refreshProduct = async () => {
    const sequence = ++refreshSequence;
    productCommerce.classList.add('is-checking');
    status.textContent = copy('checkingLabel', 'Checking current price and availability…');
    addButton.disabled = true;
    try {
      const productPath = productCommerce.dataset.productUrl.replace(/\/$/, '');
      const url = new URL(`${productPath}.js`, window.location.origin);
      url.searchParams.set('_fresh', String(Date.now()));
      const response = await fetch(url, { cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Availability check failed (${response.status})`);
      const product = await response.json();
      if (sequence !== refreshSequence) return false;
      snapshot = {
        currency: window.Shopify?.currency?.active || 'INR',
        variants: product.variants.map((variant) => ({
          id: String(variant.id),
          price: Number(variant.price),
          compareAtPrice: Number(variant.compare_at_price || 0),
          available: Boolean(variant.available),
          quantityRule: variant.quantity_rule ? {
            min: Number(variant.quantity_rule.min || 1),
            increment: Number(variant.quantity_rule.increment || 1),
            max: variant.quantity_rule.max ? Number(variant.quantity_rule.max) : null
          } : quantityRuleFromOption(variant.id)
        }))
      };
      renderProduct();
      return true;
    } catch (reason) {
      if (sequence !== refreshSequence) return false;
      console.error(reason);
      status.textContent = copy('unconfirmedLabel', 'Price and availability could not be confirmed.');
      error.textContent = copy('retryLabel', 'We could not confirm the current price and availability. Please retry.');
      error.classList.remove('is-hidden');
      addButton.disabled = true;
      return false;
    }
  };

  quantity?.addEventListener('input', renderProduct);
  quantity?.addEventListener('change', renderProduct);
  variantOptions.forEach((option) => {
    option.addEventListener('change', () => {
      if (!option.checked || !variantInput) return;
      variantInput.value = option.value;
      refreshProduct();
    });
  });
  productForm?.addEventListener('submit', async (event) => {
    if (verifiedSubmit) {
      verifiedSubmit = false;
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (await refreshProduct() && selectedVariant()?.available) {
      setLoading(true);
      verifiedSubmit = true;
      productForm.requestSubmit(addButton);
    }
  }, true);
  refreshProduct();
  window.addEventListener('pageshow', () => {
    verifiedSubmit = false;
    setLoading(false);
    refreshProduct();
  });
  window.addEventListener('focus', refreshProduct);
  window.addEventListener('online', refreshProduct);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) refreshProduct();
  });
}

document.querySelectorAll('[data-product-card]').forEach((card) => {
  const price = card.querySelector('[data-card-price]');
  const compare = card.querySelector('[data-card-compare]');
  const discount = card.querySelector('[data-card-discount]');
  const form = card.querySelector('form[action*="/cart/add"]');
  const variantInput = form?.querySelector('[name="id"]');
  const addButton = card.querySelector('[data-card-add]');
  let verifiedSubmit = false;
  const refreshCard = async () => {
    if (!card.dataset.productJsonUrl) return false;
    if (addButton) addButton.disabled = true;
    try {
      const url = new URL(card.dataset.productJsonUrl, window.location.origin);
      url.searchParams.set('_fresh', String(Date.now()));
      const response = await fetch(url, { cache: 'no-store', credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Product refresh failed (${response.status})`);
      const product = await response.json();
      const selected = product.variants.find((variant) => variant.available) || product.variants[0];
      if (price && selected) price.textContent = formatMoney(selected.price);
      if (compare && selected) {
        const comparePrice = Number(selected.compare_at_price || 0);
        compare.textContent = comparePrice > Number(selected.price) ? formatMoney(comparePrice) : '';
        compare.classList.toggle('is-hidden', comparePrice <= Number(selected.price));
        if (discount) {
          const percent = comparePrice > Number(selected.price) ? Math.round(((comparePrice - Number(selected.price)) / comparePrice) * 100) : 0;
          discount.textContent = percent > 0 ? `${percent}% off` : '';
          discount.classList.toggle('is-hidden', percent <= 0);
        }
      }
      if (variantInput && selected) variantInput.value = String(selected.id);
      if (addButton) {
        addButton.disabled = !selected?.available;
        addButton.textContent = selected?.available ? 'Add to cart' : 'Sold out';
      }
      return Boolean(selected?.available);
    } catch (reason) {
      console.error(reason);
      if (price) price.textContent = 'Check availability';
      return false;
    }
  };
  form?.addEventListener('submit', async (event) => {
    if (verifiedSubmit) {
      verifiedSubmit = false;
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (await refreshCard()) {
      verifiedSubmit = true;
      form.requestSubmit(addButton);
    }
  }, true);
  refreshCard();
  window.addEventListener('pageshow', refreshCard);
  window.addEventListener('focus', refreshCard);
  window.addEventListener('online', refreshCard);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) refreshCard();
  });
});

const cartFormWrap = document.querySelector('[data-cart-form]');
if (cartFormWrap) {
  const cartForm = cartFormWrap.querySelector('form[action*="/cart"]');
  const cartError = cartFormWrap.querySelector('[data-cart-error]');
  const cartSubtotal = cartFormWrap.querySelector('[data-cart-subtotal]');
  const discountSection = cartFormWrap.querySelector('[data-cart-discount]');
  const discountInput = discountSection?.querySelector('[data-discount-input]');
  const discountApply = discountSection?.querySelector('[data-discount-apply]');
  const discountMessage = discountSection?.querySelector('[data-discount-message]');
  const appliedDiscounts = discountSection?.querySelector('[data-applied-discounts]');
  let nativeSubmitPending = false;
  let activeDiscountCodes = Array.from(cartFormWrap.querySelectorAll('[data-discount-remove]'))
    .map((button) => button.dataset.discountCode)
    .filter(Boolean);
  const setCartBusy = (busy) => {
    cartFormWrap.classList.toggle('is-updating', busy);
    cartFormWrap.querySelectorAll('button, input').forEach((control) => { control.disabled = busy; });
  };
  const discountCopy = (name, fallback) => discountSection?.dataset[name] || fallback;
  const setDiscountMessage = (message, type = '') => {
    if (!discountMessage) return;
    discountMessage.textContent = message;
    discountMessage.className = `oc-cart-discount__message${message ? '' : ' is-hidden'}${type ? ` is-${type}` : ''}`;
  };
  const collectDiscountApplications = (cart) => {
    const collected = new Map();
    const addApplication = (application, amount) => {
      if (!application?.title) return;
      const type = application.type || '';
      const key = `${type}:${application.title.toLowerCase()}`;
      const existing = collected.get(key);
      collected.set(key, {
        ...application,
        type,
        total_allocated_amount: Number(existing?.total_allocated_amount || 0) + Number(amount || 0)
      });
    };
    (cart.cart_level_discount_applications || []).forEach((application) => {
      addApplication(application, application.total_allocated_amount);
    });
    (cart.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((allocation) => {
        addApplication(allocation.discount_application, allocation.amount);
      });
    });
    return Array.from(collected.values());
  };
  const renderDiscounts = (cart, submittedCodes = activeDiscountCodes) => {
    if (cartSubtotal) cartSubtotal.textContent = formatMoney(cart.total_price, cart.currency);
    cart.items.forEach((item, itemIndex) => {
      const lineTotal = cartFormWrap.querySelector(`[data-cart-line-index="${itemIndex + 1}"] [data-line-total]`);
      if (lineTotal) lineTotal.textContent = formatMoney(item.final_line_price, cart.currency);
    });
    if (!appliedDiscounts) return;
    const applications = collectDiscountApplications(cart);
    const submittedLookup = new Set(submittedCodes.map((code) => code.toLowerCase()));
    appliedDiscounts.replaceChildren();
    applications.forEach((application) => {
      const item = document.createElement('li');
      const title = application.title || discountCopy('appliedLabel', 'Discount applied');
      const amount = Number(application.total_allocated_amount || 0);
      const isDiscountCode = application.type === 'discount_code' || submittedLookup.has(title.toLowerCase());
      const label = document.createElement('span');
      label.textContent = amount > 0 ? `${title} · −${formatMoney(amount, cart.currency)}` : title;
      item.dataset.discountType = application.type || '';
      item.dataset.discountTitle = title;
      item.append(label);
      if (isDiscountCode) {
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.dataset.discountRemove = '';
        remove.dataset.discountCode = title;
        remove.textContent = discountCopy('removeLabel', 'Remove');
        item.append(remove);
      }
      appliedDiscounts.append(item);
    });
    activeDiscountCodes = applications
      .filter((application) => application.title && (application.type === 'discount_code' || submittedLookup.has(application.title.toLowerCase())))
      .map((application) => application.title);
    appliedDiscounts.classList.toggle('is-hidden', applications.length === 0);
  };
  const updateDiscounts = async (codes) => {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: codes.join(',') })
    });
    const cart = await response.json();
    if (!response.ok) throw new Error(cart.description || discountCopy('errorLabel', 'Discount could not be updated.'));
    renderDiscounts(cart, codes);
    return cart;
  };
  const refreshCartState = async () => {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    const cart = await response.json();
    if (!response.ok) throw new Error(cart.description || 'Cart could not be refreshed.');
    renderDiscounts(cart);
  };
  const applyDiscount = async () => {
    const code = discountInput?.value.trim();
    if (!code) {
      discountInput?.focus();
      return;
    }
    const previousCodes = [...activeDiscountCodes];
    const nextCodes = Array.from(new Set([...previousCodes, code]));
    setCartBusy(true);
    setDiscountMessage('');
    cartError?.classList.add('is-hidden');
    try {
      const cart = await updateDiscounts(nextCodes);
      const matched = collectDiscountApplications(cart).some((application) => (
        application.title?.toLowerCase() === code.toLowerCase()
      ));
      if (!matched) {
        await updateDiscounts(previousCodes);
        setDiscountMessage(discountCopy('invalidLabel', 'This code is invalid or not applicable to this cart.'), 'error');
        return;
      }
      discountInput.value = '';
      setDiscountMessage(discountCopy('successLabel', 'Discount code applied.'), 'success');
    } catch (reason) {
      console.error(reason);
      setDiscountMessage(reason.message || discountCopy('errorLabel', 'Discount could not be updated.'), 'error');
    } finally {
      setCartBusy(false);
    }
  };
  cartFormWrap.querySelectorAll('[data-cart-quantity]').forEach((input) => {
    let confirmedQuantity = Number(input.value);
    input.addEventListener('change', async () => {
      const line = input.closest('[data-cart-line]');
      const lineIndex = Number(line?.dataset.cartLineIndex);
      const nextQuantity = Math.max(0, Number(input.value || 0));
      if (!lineIndex) return;
      setCartBusy(true);
      cartError?.classList.add('is-hidden');
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ line: lineIndex, quantity: nextQuantity })
        });
        const cart = await response.json();
        if (!response.ok) throw new Error(cart.description || 'Cart could not be updated.');
        if (nextQuantity === 0) {
          window.location.reload();
          return;
        }
        const item = cart.items[lineIndex - 1];
        confirmedQuantity = item.quantity;
        input.value = String(item.quantity);
        line.querySelector('[data-line-total]').textContent = formatMoney(item.final_line_price, cart.currency);
        renderDiscounts(cart);
        document.querySelectorAll('[data-cart-count]').forEach((count) => { count.textContent = cart.item_count; });
      } catch (reason) {
        console.error(reason);
        input.value = String(confirmedQuantity);
        if (cartError) {
          cartError.textContent = reason.message || 'Cart could not be updated. Please try again.';
          cartError.classList.remove('is-hidden');
        }
      } finally {
        setCartBusy(false);
      }
    });
  });
  discountApply?.addEventListener('click', applyDiscount);
  discountInput?.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      applyDiscount();
    }
  });
  appliedDiscounts?.addEventListener('click', async (event) => {
    const remove = event.target.closest('[data-discount-remove]');
    if (!remove) return;
    const code = remove.dataset.discountCode;
    const nextCodes = activeDiscountCodes.filter((activeCode) => activeCode.toLowerCase() !== code?.toLowerCase());
    setCartBusy(true);
    setDiscountMessage('');
    try {
      await updateDiscounts(nextCodes);
      setDiscountMessage(discountCopy('appliedLabel', 'Discount updated.'), 'success');
    } catch (reason) {
      console.error(reason);
      setDiscountMessage(reason.message || discountCopy('errorLabel', 'Discount could not be updated.'), 'error');
    } finally {
      setCartBusy(false);
    }
  });
  cartForm?.addEventListener('submit', (event) => {
    if (nativeSubmitPending) {
      event.preventDefault();
      return;
    }
    nativeSubmitPending = true;
    cartFormWrap.classList.add('is-updating');
    event.submitter?.setAttribute('aria-disabled', 'true');
    window.setTimeout(() => {
      nativeSubmitPending = false;
      cartFormWrap.classList.remove('is-updating');
      event.submitter?.removeAttribute('aria-disabled');
      if (window.location.pathname.replace(/\/$/, '').endsWith('/cart') && cartError) {
        cartError.textContent = cartFormWrap.dataset.checkoutErrorLabel || 'Checkout could not be opened. Please try again.';
        cartError.classList.remove('is-hidden');
      }
    }, 10000);
  });
  refreshCartState().catch((reason) => console.error(reason));
}

const updateBuyNowButtons = () => {
  document.querySelectorAll('.shopify-payment-button__button--unbranded').forEach((button) => {
    if (button.textContent.trim() === 'Buy now' && button.getAttribute('aria-label') === 'Buy now') return;
    button.textContent = 'Buy now';
    button.setAttribute('aria-label', 'Buy now');
  });
};
updateBuyNowButtons();
new MutationObserver(updateBuyNowButtons).observe(document.documentElement, { childList: true, subtree: true });

document.querySelectorAll('[data-hero-carousel]').forEach((carousel) => {
  const slides = Array.from(carousel.querySelectorAll('[data-hero-slide]'));
  const dots = Array.from(carousel.querySelectorAll('[data-carousel-dot]'));
  const previous = carousel.querySelector('[data-carousel-previous]');
  const next = carousel.querySelector('[data-carousel-next]');
  const status = carousel.querySelector('[data-carousel-status]');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let activeIndex = 0;
  let timer;
  const showSlide = (index, announce = true) => {
    activeIndex = (index + slides.length) % slides.length;
    slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === activeIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
    });
    dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', String(dotIndex === activeIndex)));
    if (announce && status) status.textContent = dots[activeIndex].textContent.trim();
  };
  const stopRotation = () => window.clearInterval(timer);
  const startRotation = () => {
    stopRotation();
    if (!reduceMotion) timer = window.setInterval(() => showSlide(activeIndex + 1, false), 6500);
  };
  previous?.addEventListener('click', () => { showSlide(activeIndex - 1); startRotation(); });
  next?.addEventListener('click', () => { showSlide(activeIndex + 1); startRotation(); });
  dots.forEach((dot, dotIndex) => dot.addEventListener('click', () => { showSlide(dotIndex); startRotation(); }));
  carousel.addEventListener('mouseenter', stopRotation);
  carousel.addEventListener('mouseleave', startRotation);
  carousel.addEventListener('focusin', stopRotation);
  carousel.addEventListener('focusout', startRotation);
  startRotation();
});

// Product information dialogs keep long Shopify-managed content out of the purchase flow.
document.querySelectorAll('.oc-product-dialog').forEach((dialog) => {
  let returnFocus;

  const closeDialog = () => {
    if (dialog.open) dialog.close();
  };

  document.querySelectorAll(`[data-dialog-open="${dialog.id}"]`).forEach((trigger) => {
    trigger.addEventListener('click', () => {
      returnFocus = trigger;
      dialog.showModal();
      document.documentElement.classList.add('oc-dialog-open');
      dialog.querySelector('[data-dialog-close]')?.focus({ preventScroll: true });
    });
  });

  dialog.querySelectorAll('[data-dialog-close]').forEach((button) => button.addEventListener('click', closeDialog));
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) closeDialog();
  });
  dialog.addEventListener('close', () => {
    if (!document.querySelector('.oc-product-dialog[open]')) document.documentElement.classList.remove('oc-dialog-open');
    returnFocus?.focus({ preventScroll: true });
  });
});

